import React, { useState, useEffect } from 'react';
import { Card, Tabs, Row, Col, Statistic, Table, Select, Space, Tag, message, Spin, Empty } from 'antd';
import { 
  UserOutlined, 
  TeamOutlined, 
  FileTextOutlined, 
  TrophyOutlined,
  RiseOutlined,
  FallOutlined,
  DashboardOutlined
} from '@ant-design/icons';
import ReactECharts from 'echarts-for-react';
import { statisticsAPI, classAPI, studentAPI } from '../services/api';
import moment from 'moment';
import {
  buildFilters,
  compareDate,
  compareNumber,
  compareText,
  createValueFilter,
} from '../utils/tableHelpers';

const { TabPane } = Tabs;
const { Option } = Select;
// const { RangePicker } = DatePicker;

const StatisticsPage: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [dashboard, setDashboard] = useState<any>(null);
  const [classes, setClasses] = useState<any[]>([]);
  const [students, setStudents] = useState<any[]>([]);
  const [selectedClass, setSelectedClass] = useState<number | undefined>();
  const [selectedStudent, setSelectedStudent] = useState<number | undefined>();
  const [classStats, setClassStats] = useState<any>(null);
  const [studentStats, setStudentStats] = useState<any>(null);

  useEffect(() => {
    loadInitialData();
  }, []);

  const loadInitialData = async () => {
    setLoading(true);
    try {
      // 加载仪表板数据
      const dashboardRes = await statisticsAPI.teacherDashboard();
      setDashboard(dashboardRes.data);

      // 加载班级列表
      const classRes = await classAPI.list({ page_size: 100 });
      setClasses(classRes.data.data || []);

      // 加载学生列表
      const studentRes = await studentAPI.list({ page_size: 100 });
      setStudents(studentRes.data.data || []);
    } catch (error) {
      message.error('加载数据失败');
    } finally {
      setLoading(false);
    }
  };

  // 加载班级统计
  const loadClassStats = async (classId: number) => {
    setLoading(true);
    try {
      const res = await statisticsAPI.classStatistics(classId);
      setClassStats(res.data);
    } catch (error) {
      message.error('加载班级统计失败');
    } finally {
      setLoading(false);
    }
  };

  // 加载学生统计
  const loadStudentStats = async (studentId: number) => {
    setLoading(true);
    try {
      const res = await statisticsAPI.studentProgress(studentId);
      setStudentStats(res.data);
    } catch (error) {
      message.error('加载学生统计失败');
    } finally {
      setLoading(false);
    }
  };

  // 渲染仪表板
  const renderDashboard = () => {
    if (!dashboard) return <Empty description="暂无数据" />;

    const { overview, weekly_trend, top_students, recent_activity } = dashboard;

    // 周趋势图表配置
    const trendOption = {
      title: { text: '本周趋势' },
      tooltip: { trigger: 'axis' },
      legend: { data: ['提交数', '平均分'] },
      xAxis: { type: 'category', data: weekly_trend?.dates || [] },
      yAxis: [
        { type: 'value', name: '提交数' },
        { type: 'value', name: '平均分' }
      ],
      series: [
        {
          name: '提交数',
          type: 'bar',
          data: weekly_trend?.submissions || []
        },
        {
          name: '平均分',
          type: 'line',
          yAxisIndex: 1,
          data: weekly_trend?.average_scores || []
        }
      ]
    };

    return (
      <div>
        <Row gutter={16}>
          <Col span={6}>
            <Card>
              <Statistic
                title="班级总数"
                value={overview?.total_classes || 0}
                prefix={<TeamOutlined />}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic
                title="学生总数"
                value={overview?.total_students || 0}
                prefix={<UserOutlined />}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic
                title="作文总数"
                value={overview?.total_essays || 0}
                prefix={<FileTextOutlined />}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic
                title="平均分"
                value={overview?.average_score || 0}
                precision={2}
                suffix="/ 25"
                prefix={<TrophyOutlined />}
              />
            </Card>
          </Col>
        </Row>

        <Row gutter={16} style={{ marginTop: 16 }}>
          <Col span={12}>
            <Card title="周趋势分析">
              <ReactECharts option={trendOption} style={{ height: 300 }} />
            </Card>
          </Col>
          <Col span={12}>
            <Card title="优秀学生排行">
              <Table
                dataSource={top_students}
                columns={[
                  {
                    title: '排名',
                    dataIndex: 'rank',
                    key: 'rank',
                    width: 60,
                    sorter: compareNumber((record: any) => record.rank),
                  },
                  {
                    title: '姓名',
                    dataIndex: 'student_name',
                    key: 'student_name',
                    sorter: compareText((record: any) => record.student_name),
                    filters: buildFilters(top_students || [], (record: any) => record.student_name),
                    onFilter: createValueFilter((record: any) => record.student_name),
                    filterSearch: true,
                  },
                  {
                    title: '班级',
                    dataIndex: 'class_name',
                    key: 'class_name',
                    sorter: compareText((record: any) => record.class_name),
                    filters: buildFilters(top_students || [], (record: any) => record.class_name),
                    onFilter: createValueFilter((record: any) => record.class_name),
                    filterSearch: true,
                  },
                  { 
                    title: '平均分', 
                    dataIndex: 'average_score', 
                    key: 'average_score',
                    sorter: compareNumber((record: any) => record.average_score),
                    render: (score: number) => score?.toFixed(2) || '-'
                  },
                  { 
                    title: '进步', 
                    dataIndex: 'improvement', 
                    key: 'improvement',
                    sorter: compareNumber((record: any) => record.improvement),
                    render: (val: number) => (
                      <span style={{ color: val > 0 ? 'green' : 'red' }}>
                        {val > 0 ? <RiseOutlined /> : <FallOutlined />}
                        {Math.abs(val || 0).toFixed(1)}
                      </span>
                    )
                  }
                ]}
                pagination={false}
                size="small"
                rowKey={(record: any) => `${record.student_name}-${record.class_name}-${record.rank}`}
              />
            </Card>
          </Col>
        </Row>

        <Card title="最近活动" style={{ marginTop: 16 }}>
          <Table
            dataSource={recent_activity}
            columns={[
              { 
                title: '时间', 
                dataIndex: 'time', 
                key: 'time',
                sorter: compareDate((record: any) => record.time),
                render: (time: string) => moment(time).format('MM-DD HH:mm')
              },
              {
                title: '学生',
                dataIndex: 'student_name',
                key: 'student_name',
                sorter: compareText((record: any) => record.student_name),
                filters: buildFilters(recent_activity || [], (record: any) => record.student_name),
                onFilter: createValueFilter((record: any) => record.student_name),
                filterSearch: true,
              },
              {
                title: '班级',
                dataIndex: 'class_name',
                key: 'class_name',
                sorter: compareText((record: any) => record.class_name),
                filters: buildFilters(recent_activity || [], (record: any) => record.class_name),
                onFilter: createValueFilter((record: any) => record.class_name),
                filterSearch: true,
              },
              {
                title: '作文',
                dataIndex: 'essay_title',
                key: 'essay_title',
                sorter: compareText((record: any) => record.essay_title),
                filters: buildFilters(recent_activity || [], (record: any) => record.essay_title),
                onFilter: createValueFilter((record: any) => record.essay_title),
                filterSearch: true,
              },
              { 
                title: '类型', 
                dataIndex: 'type', 
                key: 'type',
                filters: [
                  { text: '已批改', value: 'correction' },
                  { text: '新提交', value: 'submission' },
                ],
                onFilter: (value: boolean | string | number | bigint, record: any) => record.type === value,
                render: (type: string) => (
                  <Tag color={type === 'correction' ? 'green' : 'blue'}>
                    {type === 'correction' ? '已批改' : '新提交'}
                  </Tag>
                )
              },
              { 
                title: '分数', 
                dataIndex: 'score', 
                key: 'score',
                sorter: compareNumber((record: any) => record.score),
                render: (score: number) => score ? `${score.toFixed(1)}/25` : '-'
              }
            ]}
            pagination={{ pageSize: 5 }}
            size="small"
            rowKey={(record: any) => `${record.time}-${record.student_name}-${record.essay_title}`}
          />
        </Card>
      </div>
    );
  };

  // 渲染班级统计
  const renderClassStats = () => {
    if (!selectedClass) {
      return (
        <Empty description="请选择班级查看统计">
          <Select
            style={{ width: 200 }}
            placeholder="选择班级"
            onChange={(value) => {
              setSelectedClass(value);
              loadClassStats(value);
            }}
          >
            {classes.map(cls => (
              <Option key={cls.id} value={cls.id}>
                {cls.name} ({cls.student_count}人)
              </Option>
            ))}
          </Select>
        </Empty>
      );
    }

    if (!classStats) return <Empty description="暂无数据" />;

    const { stats, rankings, recent_essays } = classStats;

    // 学生排名图表
    const rankingOption = {
      title: { text: '学生成绩分布' },
      tooltip: {},
      xAxis: { 
        type: 'category', 
        data: rankings?.map((r: any) => r.name) || []
      },
      yAxis: { type: 'value', name: '平均分' },
      series: [{
        type: 'bar',
        data: rankings?.map((r: any) => r.score) || [],
        itemStyle: {
          color: (params: any) => {
            const colors = ['#ff4d4f', '#ff7a45', '#ffa940', '#ffc53d', '#ffec3d'];
            return colors[params.dataIndex] || '#1890ff';
          }
        }
      }]
    };

    return (
      <div>
        <Space style={{ marginBottom: 16 }}>
          <Select
            style={{ width: 200 }}
            value={selectedClass}
            onChange={(value) => {
              setSelectedClass(value);
              loadClassStats(value);
            }}
          >
            {classes.map(cls => (
              <Option key={cls.id} value={cls.id}>
                {cls.name} ({cls.student_count}人)
              </Option>
            ))}
          </Select>
        </Space>

        <Row gutter={16}>
          <Col span={6}>
            <Card>
              <Statistic title="学生数" value={stats?.total_students || 0} />
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic title="作文数" value={stats?.total_essays || 0} />
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic 
                title="平均分" 
                value={stats?.average_score || 0} 
                precision={2}
                suffix="/ 25"
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic 
                title="完成率" 
                value={stats?.completion_rate || 0} 
                precision={1}
                suffix="%"
              />
            </Card>
          </Col>
        </Row>

        <Card title="成绩分布" style={{ marginTop: 16 }}>
          <ReactECharts option={rankingOption} style={{ height: 300 }} />
        </Card>

        <Card title="最近提交" style={{ marginTop: 16 }}>
          <Table
            dataSource={recent_essays}
            columns={[
              {
                title: '学生',
                dataIndex: 'student_name',
                key: 'student_name',
                sorter: compareText((record: any) => record.student_name),
                filters: buildFilters(recent_essays || [], (record: any) => record.student_name),
                onFilter: createValueFilter((record: any) => record.student_name),
                filterSearch: true,
              },
              {
                title: '作文题目',
                dataIndex: 'title',
                key: 'title',
                sorter: compareText((record: any) => record.title),
                filters: buildFilters(recent_essays || [], (record: any) => record.title),
                onFilter: createValueFilter((record: any) => record.title),
                filterSearch: true,
              },
              { 
                title: '状态', 
                dataIndex: 'status', 
                key: 'status',
                filters: [
                  { text: '已完成', value: 'completed' },
                  { text: '待批改', value: 'pending' },
                ],
                onFilter: (value: boolean | string | number | bigint, record: any) => (
                  value === 'completed'
                    ? record.status === 'completed'
                    : record.status !== 'completed'
                ),
                render: (status: string) => (
                  <Tag color={status === 'completed' ? 'green' : 'orange'}>
                    {status === 'completed' ? '已完成' : '待批改'}
                  </Tag>
                )
              },
              { 
                title: '提交时间', 
                dataIndex: 'submitted_at', 
                key: 'submitted_at',
                sorter: compareDate((record: any) => record.submitted_at),
                render: (time: string) => moment(time).format('YYYY-MM-DD HH:mm')
              }
            ]}
            pagination={{ pageSize: 5 }}
            size="small"
            rowKey={(record: any) => `${record.student_name}-${record.title}-${record.submitted_at}`}
          />
        </Card>
      </div>
    );
  };

  // 渲染学生统计
  const renderStudentStats = () => {
    if (!selectedStudent) {
      return (
        <Empty description="请选择学生查看统计">
          <Select
            style={{ width: 300 }}
            placeholder="选择学生"
            showSearch
            optionFilterProp="children"
            onChange={(value) => {
              setSelectedStudent(value);
              loadStudentStats(value);
            }}
          >
            {students.map(student => (
              <Option key={student.id} value={student.id}>
                {student.name} - {student.class?.name || '未分班'}
              </Option>
            ))}
          </Select>
        </Empty>
      );
    }

    if (!studentStats) return <Empty description="暂无数据" />;

    const { student, stats, progress } = studentStats;

    // 进步曲线图
    const progressOption = {
      title: { text: '成绩趋势' },
      tooltip: { trigger: 'axis' },
      xAxis: { 
        type: 'category', 
        data: progress?.map((p: any, i: number) => `第${i + 1}次`) || []
      },
      yAxis: { type: 'value', name: '分数', max: 25 },
      series: [{
        type: 'line',
        data: progress?.map((p: any) => p.total_score) || [],
        smooth: true,
        markLine: {
          data: [{ type: 'average', name: '平均分' }]
        }
      }]
    };

    return (
      <div>
        <Space style={{ marginBottom: 16 }}>
          <Select
            style={{ width: 300 }}
            value={selectedStudent}
            showSearch
            optionFilterProp="children"
            onChange={(value) => {
              setSelectedStudent(value);
              loadStudentStats(value);
            }}
          >
            {students.map(student => (
              <Option key={student.id} value={student.id}>
                {student.name} - {student.class?.name || '未分班'}
              </Option>
            ))}
          </Select>
        </Space>

        <Card title="学生信息">
          <Row gutter={16}>
            <Col span={6}>
              <Statistic title="姓名" value={student?.name} />
            </Col>
            <Col span={6}>
              <Statistic title="班级" value={stats?.class_name || '-'} />
            </Col>
            <Col span={6}>
              <Statistic title="作文数" value={stats?.total_essays || 0} />
            </Col>
            <Col span={6}>
              <Statistic 
                title="班级排名" 
                value={stats?.rank_in_class || '-'} 
                suffix={`/ ${classes.find(c => c.id === student?.class_id)?.student_count || '-'}`}
              />
            </Col>
          </Row>
        </Card>

        <Row gutter={16} style={{ marginTop: 16 }}>
          <Col span={12}>
            <Card title="成绩统计">
              <Row gutter={16}>
                <Col span={12}>
                  <Statistic 
                    title="平均分" 
                    value={stats?.average_score || 0} 
                    precision={2}
                    suffix="/ 25"
                  />
                </Col>
                <Col span={12}>
                  <Statistic 
                    title="最新分数" 
                    value={stats?.latest_score || 0} 
                    precision={2}
                    suffix="/ 25"
                  />
                </Col>
              </Row>
              <Row gutter={16} style={{ marginTop: 16 }}>
                <Col span={12}>
                  <Statistic 
                    title="最高分" 
                    value={stats?.highest_score || 0} 
                    precision={2}
                    suffix="/ 25"
                    valueStyle={{ color: '#3f8600' }}
                  />
                </Col>
                <Col span={12}>
                  <Statistic 
                    title="进步幅度" 
                    value={stats?.improvement || 0} 
                    precision={2}
                    prefix={stats?.improvement > 0 ? '+' : ''}
                    valueStyle={{ color: stats?.improvement > 0 ? '#3f8600' : '#cf1322' }}
                  />
                </Col>
              </Row>
            </Card>
          </Col>
          <Col span={12}>
            <Card title="成绩趋势">
              <ReactECharts option={progressOption} style={{ height: 200 }} />
            </Card>
          </Col>
        </Row>

        <Card title="最近作文" style={{ marginTop: 16 }}>
          <Table
            dataSource={stats?.recent_essays || []}
            columns={[
              {
                title: '作文题目',
                dataIndex: 'title',
                key: 'title',
                sorter: compareText((record: any) => record.title),
                filters: buildFilters(stats?.recent_essays || [], (record: any) => record.title),
                onFilter: createValueFilter((record: any) => record.title),
                filterSearch: true,
              },
              { 
                title: '分数', 
                dataIndex: 'score', 
                key: 'score',
                sorter: compareNumber((record: any) => record.score),
                render: (score: number) => score ? `${score.toFixed(1)}/25` : '待批改'
              },
              { 
                title: '状态', 
                dataIndex: 'status', 
                key: 'status',
                filters: [
                  { text: '已完成', value: 'completed' },
                  { text: '待批改', value: 'pending' },
                ],
                onFilter: (value: boolean | string | number | bigint, record: any) => (
                  value === 'completed'
                    ? record.status === 'completed'
                    : record.status !== 'completed'
                ),
                render: (status: string) => (
                  <Tag color={status === 'completed' ? 'green' : 'orange'}>
                    {status === 'completed' ? '已完成' : '待批改'}
                  </Tag>
                )
              },
              { 
                title: '提交时间', 
                dataIndex: 'submitted_at', 
                key: 'submitted_at',
                sorter: compareDate((record: any) => record.submitted_at),
                render: (time: string) => moment(time).format('YYYY-MM-DD')
              }
            ]}
            pagination={false}
            size="small"
            rowKey={(record: any) => `${record.title}-${record.submitted_at}`}
          />
        </Card>
      </div>
    );
  };

  return (
    <div className="statistics-page">
      <Card>
        <Tabs activeKey={activeTab} onChange={setActiveTab}>
          <TabPane 
            tab={<span><DashboardOutlined /> 总览</span>} 
            key="dashboard"
          >
            <Spin spinning={loading}>
              {renderDashboard()}
            </Spin>
          </TabPane>
          
          <TabPane 
            tab={<span><TeamOutlined /> 班级分析</span>} 
            key="class"
          >
            <Spin spinning={loading}>
              {renderClassStats()}
            </Spin>
          </TabPane>
          
          <TabPane 
            tab={<span><UserOutlined /> 学生分析</span>} 
            key="student"
          >
            <Spin spinning={loading}>
              {renderStudentStats()}
            </Spin>
          </TabPane>
        </Tabs>
      </Card>
    </div>
  );
};

export default StatisticsPage;
